/**
 * "Still there?" — signs a shared device out after it has been left alone.
 *
 * The next person at a shop-floor PC should not inherit the previous one's
 * name, chat or half-filled ticket. After {@link KIOSK_IDLE_MS} with no input
 * this asks, and signs out {@link KIOSK_WARNING_MS} later unless someone
 * answers (1 Oct). Activity while it is open does not count: a passer-by
 * brushing the screen must not quietly keep the session alive, so only the
 * buttons close it. The timing itself is `kioskIdleState`, kept pure for tests.
 *
 * Mounted only on kiosk sessions (`KioskGate`).
 */

import { AlertDialog, Button } from "@norican/react";
import { useEffect, useRef, useState } from "react";

import { useSignOut } from "@/components/layout";
import { useT } from "@/i18n";
import { KIOSK_IDLE_MS, KIOSK_WARNING_MS, kioskIdleState } from "@/lib/kiosk";
import { logger } from "@/lib/logger";

/** Inputs that count as "someone is here". */
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel", "mousemove"] as const;

/** How often the timer is re-checked; also the countdown's resolution. */
const TICK_MS = 1000;

export function KioskIdleGuard() {
  const { t } = useT();
  const { signOut, isPending } = useSignOut();
  const lastActivityAt = useRef(Date.now());
  const [warningStartedAt, setWarningStartedAt] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(KIOSK_WARNING_MS / 1000));
  const signingOut = useRef(false);

  useEffect(() => {
    const onActivity = () => {
      lastActivityAt.current = Date.now();
    };
    for (const name of ACTIVITY_EVENTS) {
      window.addEventListener(name, onActivity, { passive: true });
    }
    return () => {
      for (const name of ACTIVITY_EVENTS) {
        window.removeEventListener(name, onActivity);
      }
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const state = kioskIdleState({
        now: Date.now(),
        lastActivityAt: lastActivityAt.current,
        warningStartedAt,
        idleMs: KIOSK_IDLE_MS,
        warningMs: KIOSK_WARNING_MS,
      });
      if (state.phase === "warning" && warningStartedAt === null) {
        logger.debug("[kiosk] idle; asking if anyone is still there");
        setWarningStartedAt(Date.now());
      }
      setSecondsLeft(state.secondsLeft);
      if (state.phase === "expired" && !signingOut.current) {
        signingOut.current = true;
        logger.debug("[kiosk] nobody answered; signing the shared device out");
        void signOut();
      }
    }, TICK_MS);
    return () => window.clearInterval(timer);
  }, [warningStartedAt, signOut]);

  const stay = () => {
    lastActivityAt.current = Date.now();
    setWarningStartedAt(null);
  };

  return (
    <AlertDialog.Root
      open={warningStartedAt !== null}
      // Only the two buttons close it: neither Esc nor a stray touch counts as
      // an answer from the person who walked away.
      onOpenChange={() => undefined}
    >
      <AlertDialog.Portal>
        <AlertDialog.Backdrop />
        <AlertDialog.Popup className="flex w-full max-w-md flex-col gap-4">
          <AlertDialog.Title>{t("kiosk.idle.title")}</AlertDialog.Title>
          <AlertDialog.Description>
            {t("kiosk.idle.countdown", { count: secondsLeft })}
          </AlertDialog.Description>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="ghost"
              tone="neutral"
              loading={isPending}
              disabled={isPending}
              onClick={() => {
                signingOut.current = true;
                void signOut();
              }}
            >
              {t("kiosk.idle.signOut")}
            </Button>
            <Button onClick={stay} disabled={isPending}>
              {t("kiosk.idle.stay")}
            </Button>
          </div>
        </AlertDialog.Popup>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
